import { supabase } from '../lib/supabase';

export class StorageService {
  static async uploadUserAvatar(userId, file) {
    try {
      // Validate inputs
      if (!userId || !file) {
        throw new Error('User ID and file are required');
      }
      
      this.validateImageFile(file);
      
      const compressedFile = await this.compressImage(file, 200, 200, 0.8);
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (!fileExt) {
        throw new Error('Invalid file format');
      }
      
      // Use timestamp to ensure unique filename and avoid browser caching
      const timestamp = Date.now();
      const fileName = `${userId}/avatar_${timestamp}.${fileExt}`;

      // Delete existing avatar if it exists (automatic cleanup)
      await this.deleteUserAvatarFiles(userId);

      const { data, error } = await supabase.storage
        .from('user-avatars')
        .upload(fileName, compressedFile, {
          cacheControl: '604800', // 7 days cache for avatars
          upsert: false // Don't upsert since we're using unique filenames
        });

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      const { data: urlData } = supabase.storage
        .from('user-avatars')
        .getPublicUrl(fileName);

      if (!urlData?.publicUrl) {
        throw new Error('Failed to generate public URL');
      }

      // Update user profile with avatar URL
      const { error: updateError } = await supabase
        .from('users')
        .update({ avatar_url: urlData.publicUrl })
        .eq('id', userId);

      if (updateError) {
        // Clean up uploaded file if database update fails
        await supabase.storage.from('user-avatars').remove([fileName]);
        throw new Error(`Database update failed: ${updateError.message}`);
      }

      return { url: urlData.publicUrl, path: data.path };
    } catch (error) {
      console.error('Error uploading user avatar:', error);
      throw error instanceof Error ? error : new Error('Unknown upload error');
    }
  }

  static async uploadTeamLogo(teamId, file) {
    try {
      // Validate inputs
      if (!teamId || !file) {
        throw new Error('Team ID and file are required');
      }
      
      // Validate team membership and permissions
      const { data: userData } = await supabase.auth.getUser();
      
      const { data: teamMember, error: memberError } = await supabase
        .from('team_members')
        .select('role, team_id')
        .eq('team_id', teamId)
        .eq('user_id', userData.user?.id)
        .single();
      
      if (memberError || !teamMember) {
        throw new Error(`You are not a member of team ${teamId}`);
      }
      
      if (!['owner', 'captain'].includes(teamMember.role)) {
        throw new Error(`Insufficient permissions. Your role: ${teamMember.role}`);
      }
      
      this.validateImageFile(file);
      
      const compressedFile = await this.compressImage(file, 200, 200, 0.8);
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (!fileExt) {
        throw new Error('Invalid file format');
      }
      
      // Use timestamp to ensure unique filename and avoid browser caching
      const timestamp = Date.now();
      const fileName = `${teamId}/logo_${timestamp}.${fileExt}`;
      
      // Continue with upload

      // Delete existing logo if it exists
      await this.deleteTeamLogoFiles(teamId);

      const { data, error } = await supabase.storage
        .from('team-avatars')
        .upload(fileName, compressedFile, {
          cacheControl: '604800', // 7 days cache for avatars
          upsert: false // Don't upsert since we're using unique filenames
        });

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      const { data: urlData } = supabase.storage
        .from('team-avatars')
        .getPublicUrl(fileName);

      if (!urlData?.publicUrl) {
        throw new Error('Failed to generate public URL');
      }

      // Update database with new logo URL
      const { error: updateError } = await supabase
        .from('teams')
        .update({ logo: urlData.publicUrl })
        .eq('id', teamId);

      if (updateError) {
        // Clean up uploaded file if database update fails
        await supabase.storage.from('team-avatars').remove([fileName]);
        throw new Error(`Database update failed: ${updateError.message}`);
      }

      return { url: urlData.publicUrl, path: data.path, fileName };
    } catch (error) {
      console.error('Error uploading team logo:', error);
      throw error instanceof Error ? error : new Error('Unknown upload error');
    }
  }

  static async deleteUserAvatarFiles(userId) {
    try {
      const { data: files } = await supabase.storage
        .from('user-avatars')
        .list(userId);

      if (files && files.length > 0) {
        const filePaths = files.map(file => `${userId}/${file.name}`);
        await supabase.storage
          .from('user-avatars')
          .remove(filePaths);
      }
    } catch (error) {
      console.error('Error deleting user avatar files:', error);
      // Don't throw error for file cleanup
    }
  }

  static async deleteUserAvatar(userId) {
    try {
      await this.deleteUserAvatarFiles(userId);

      // Update profile to remove avatar URL
      await supabase
        .from('users')
        .update({ avatar_url: null })
        .eq('id', userId);

    } catch (error) {
      console.error('Error deleting user avatar:', error);
      throw error;
    }
  }

  static async deleteTeamLogoFiles(teamId) {
    try {
      const { data: files } = await supabase.storage
        .from('team-avatars')
        .list(teamId);

      if (files && files.length > 0) {
        const filePaths = files.map(file => `${teamId}/${file.name}`);
        await supabase.storage
          .from('team-avatars')
          .remove(filePaths);
      }
    } catch (error) {
      console.error('Error deleting team logo files:', error);
      // Don't throw error for file cleanup
    }
  }

  static async deleteTeamLogo(teamId) {
    try {
      await this.deleteTeamLogoFiles(teamId);

      // Update team to remove logo (reset to null)
      await supabase
        .from('teams')
        .update({ logo: null })
        .eq('id', teamId);

    } catch (error) {
      console.error('Error deleting team logo:', error);
      throw error;
    }
  }

  static async compressImage(file, maxWidth, maxHeight, quality = 0.9) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = async () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Calculate optimal dimensions maintaining aspect ratio
          let { width, height } = img;
          const aspectRatio = width / height;
          
          // Only downscale if image is larger than max dimensions
          if (width > maxWidth || height > maxHeight) {
            if (aspectRatio > 1) {
              width = maxWidth;
              height = maxWidth / aspectRatio;
            } else {
              height = maxHeight;
              width = maxHeight * aspectRatio;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // Use high-quality image rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Draw image without background (preserves transparency)
          ctx.drawImage(img, 0, 0, width, height);

          // Test different formats and pick the smallest
          const formats = await this.testImageFormats(canvas, file, quality);
          const bestFormat = this.selectBestFormat(formats);

          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          }, bestFormat.mimeType, bestFormat.quality);
        } catch (error) {
          reject(new Error(`Image compression failed: ${error.message}`));
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image for compression'));
      };
      
      img.src = URL.createObjectURL(file);
    });
  }

  static async testImageFormats(canvas, originalFile, baseQuality) {
    const formats = [];
    
    // Check if image has transparency
    const hasTransparency = await this.hasTransparency(canvas);
    
    // Test WebP (best compression for most cases)
    try {
      const webpBlob = await this.canvasToBlob(canvas, 'image/webp', baseQuality);
      if (webpBlob) {
        formats.push({
          format: 'webp',
          mimeType: 'image/webp',
          size: webpBlob.size,
          quality: baseQuality,
          blob: webpBlob
        });
      }
    } catch (e) {
      // WebP not supported, continue
    }

    // Test PNG if transparency or simple graphics
    if (hasTransparency || this.isSimpleGraphic(originalFile)) {
      try {
        const pngBlob = await this.canvasToBlob(canvas, 'image/png', 1.0);
        if (pngBlob) {
          formats.push({
            format: 'png',
            mimeType: 'image/png',
            size: pngBlob.size,
            quality: 1.0,
            blob: pngBlob
          });
        }
      } catch (e) {
        // Continue if PNG fails
      }
    }

    // Test JPEG (good for photos, no transparency)
    if (!hasTransparency) {
      try {
        const jpegBlob = await this.canvasToBlob(canvas, 'image/jpeg', baseQuality);
        if (jpegBlob) {
          formats.push({
            format: 'jpeg',
            mimeType: 'image/jpeg',
            size: jpegBlob.size,
            quality: baseQuality,
            blob: jpegBlob
          });
        }
      } catch (e) {
        // Continue if JPEG fails
      }
    }

    return formats;
  }

  static canvasToBlob(canvas, mimeType, quality) {
    return new Promise((resolve) => {
      canvas.toBlob(resolve, mimeType, quality);
    });
  }

  static selectBestFormat(formats) {
    if (formats.length === 0) {
      // Fallback to JPEG
      return { mimeType: 'image/jpeg', quality: 0.9 };
    }

    // Sort by file size (smallest first)
    formats.sort((a, b) => a.size - b.size);
    
    // Return the format with smallest file size
    return {
      mimeType: formats[0].mimeType,
      quality: formats[0].quality
    };
  }

  static async hasTransparency(canvas) {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Check alpha channel for any non-255 values
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) {
        return true;
      }
    }
    return false;
  }

  static isSimpleGraphic(file) {
    // Heuristic: if original was PNG and small, likely a logo/icon
    return file.type === 'image/png' && file.size < 100000; // Less than 100KB
  }

  static async uploadTournamentLogo(tournamentId, file) {
    try {
      // Validate inputs
      if (!tournamentId || !file) {
        throw new Error('Tournament ID and file are required');
      }
      
      
      this.validateImageFile(file);
      
      const compressedFile = await this.compressImage(file, 200, 200, 0.8);
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (!fileExt) {
        throw new Error('Invalid file format');
      }
      
      // Use timestamp to ensure unique filename and avoid browser caching
      const timestamp = Date.now();
      const fileName = `${tournamentId}/logo_${timestamp}.${fileExt}`;

      // Delete existing logo if it exists
      await this.deleteTournamentLogoFiles(tournamentId);

      const { data, error } = await supabase.storage
        .from('tournament-images')
        .upload(fileName, compressedFile, {
          cacheControl: '604800', // 7 days cache for avatars
          upsert: false // Don't upsert since we're using unique filenames
        });

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      const { data: urlData } = supabase.storage
        .from('tournament-images')
        .getPublicUrl(fileName);

      if (!urlData?.publicUrl) {
        throw new Error('Failed to generate public URL');
      }

      // Update database with new logo URL
      const { error: updateError } = await supabase
        .from('tournaments')
        .update({ logo: urlData.publicUrl })
        .eq('id', tournamentId);

      if (updateError) {
        // Clean up uploaded file if database update fails
        await supabase.storage.from('tournament-images').remove([fileName]);
        throw new Error(`Database update failed: ${updateError.message}`);
      }

      return { url: urlData.publicUrl, path: data.path, fileName };
    } catch (error) {
      console.error('Error uploading tournament logo:', error);
      throw error instanceof Error ? error : new Error('Unknown upload error');
    }
  }

  static async deleteTournamentLogoFiles(tournamentId) {
    try {
      const { data: files } = await supabase.storage
        .from('tournament-images')
        .list(tournamentId);

      if (files && files.length > 0) {
        const filePaths = files.map(file => `${tournamentId}/${file.name}`);
        await supabase.storage
          .from('tournament-images')
          .remove(filePaths);
      }
    } catch (error) {
      console.error('Error deleting tournament logo files:', error);
      // Don't throw error for file cleanup
    }
  }

  static async deleteTournamentLogo(tournamentId) {
    try {
      await this.deleteTournamentLogoFiles(tournamentId);

      // Update tournament to remove logo (reset to null)
      const { error: updateError } = await supabase
        .from('tournaments')
        .update({ logo: null })
        .eq('id', tournamentId);

      if (updateError) {
        throw new Error(`Database update failed: ${updateError.message}`);
      }

    } catch (error) {
      console.error('Error deleting tournament logo:', error);
      throw error;
    }
  }

  static validateImageFile(file) {
    if (!file || !(file instanceof File)) {
      throw new Error('Invalid file object');
    }
    
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    const minSize = 1024; // 1KB minimum

    if (!validTypes.includes(file.type.toLowerCase())) {
      throw new Error('Invalid file type. Please upload JPEG, PNG, or WebP images.');
    }

    if (file.size > maxSize) {
      throw new Error('File too large. Please upload images smaller than 5MB.');
    }
    
    if (file.size < minSize) {
      throw new Error('File too small. Please upload a valid image file.');
    }

    return true;
  }

  /**
   * Validate screenshot files with specific requirements for AI processing
   */
  static validateScreenshotFile(file) {
    if (!file || !(file instanceof File)) {
      throw new Error('Invalid file object');
    }
    
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB for screenshots (larger than avatars)
    const minSize = 10 * 1024; // 10KB minimum (larger than avatars)

    if (!validTypes.includes(file.type.toLowerCase())) {
      throw new Error('Invalid file type. Please upload JPEG, PNG, or WebP screenshots.');
    }

    if (file.size > maxSize) {
      throw new Error('Screenshot too large. Please upload images smaller than 10MB.');
    }
    
    if (file.size < minSize) {
      throw new Error('Screenshot too small. Please upload a clearer screenshot.');
    }

    return true;
  }

  /**
   * Compress image specifically for AI processing
   * Optimized for text readability and small file size
   */
  static async compressForAI(file, maxWidth = 1200, maxHeight = 800, quality = 0.85) {
    try {
      this.validateScreenshotFile(file);
      
      // Use higher resolution than avatars to maintain text readability
      const compressedFile = await this.compressImage(file, maxWidth, maxHeight, quality);
      
      // Ensure the compressed file is still readable (not too small)
      if (compressedFile.size < 50 * 1024) { // 50KB minimum
        console.warn('Compressed screenshot may be too small for AI processing');
      }
      
      return compressedFile;
    } catch (error) {
      console.error('Error compressing screenshot for AI:', error);
      throw error instanceof Error ? error : new Error('Screenshot compression failed');
    }
  }

  /**
   * Convert file to base64 string for API transmission
   */
  static fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          // Remove the data URL prefix (data:image/jpeg;base64,)
          const base64 = result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };
      reader.onerror = () => reject(new Error('File read error'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Process screenshot for AI analysis
   * Returns compressed file and base64 string ready for API
   */
  static async processScreenshotForAI(file) {
    try {
      // Compress for optimal AI processing
      const compressedFile = await this.compressForAI(file);
      
      // Convert to base64 for API transmission
      const base64 = await this.fileToBase64(compressedFile);
      
      return {
        compressedFile,
        base64,
        originalSize: file.size,
        compressedSize: compressedFile.size,
        compressionRatio: ((file.size - compressedFile.size) / file.size * 100).toFixed(1)
      };
    } catch (error) {
      console.error('Error processing screenshot for AI:', error);
      throw error instanceof Error ? error : new Error('Screenshot processing failed');
    }
  }

  /**
   * Upload match screenshot evidence
   * Follows same patterns as team/user uploads but for match evidence
   */
  static async uploadMatchScreenshot(matchId, file, teamNumber, submittedBy = null) {
    try {
      // Validate inputs
      if (!matchId || !file || !teamNumber) {
        throw new Error('Match ID, file, and team number are required');
      }

      if (![1, 2].includes(teamNumber)) {
        throw new Error('Team number must be 1 or 2');
      }

      // Validate the file as a screenshot
      this.validateScreenshotFile(file);

      // Get current user if not provided
      const userId = submittedBy || (await supabase.auth.getUser()).data.user?.id;
      if (!userId) {
        throw new Error('User authentication required');
      }

      // Compress image optimized for screenshots (higher resolution than avatars)
      const compressedFile = await this.compressForAI(file, 1200, 800, 0.9);
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (!fileExt) {
        throw new Error('Invalid file format');
      }

      // Create filename following match-evidence bucket structure
      const timestamp = Date.now();
      const fileName = `${matchId}/team${teamNumber}_screenshot_${timestamp}.${fileExt}`;

      // Upload to match-evidence bucket
      const { data, error } = await supabase.storage
        .from('match-evidence')
        .upload(fileName, compressedFile, {
          cacheControl: '604800', // 7 days cache
          upsert: false // Don't overwrite - use unique timestamps
        });

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('match-evidence')
        .getPublicUrl(fileName);

      if (!urlData?.publicUrl) {
        throw new Error('Failed to generate public URL');
      }

      return {
        url: urlData.publicUrl,
        path: data.path,
        fileName,
        teamNumber,
        uploadedBy: userId,
        originalSize: file.size,
        compressedSize: compressedFile.size,
        compressionRatio: ((file.size - compressedFile.size) / file.size * 100).toFixed(1)
      };
    } catch (error) {
      console.error('Error uploading match screenshot:', error);
      throw error instanceof Error ? error : new Error('Match screenshot upload failed');
    }
  }

  /**
   * Delete match screenshot files from storage
   * Used for cleanup when match results are updated
   */
  static async deleteMatchScreenshotFiles(matchId) {
    try {
      const { data: files } = await supabase.storage
        .from('match-evidence')
        .list(matchId);

      if (files && files.length > 0) {
        const filePaths = files.map(file => `${matchId}/${file.name}`);
        await supabase.storage
          .from('match-evidence')
          .remove(filePaths);
      }
    } catch (error) {
      console.error('Error deleting match screenshot files:', error);
      // Don't throw error for file cleanup operations
    }
  }

  /**
   * Delete specific match screenshot
   * Used when removing individual evidence files
   */
  static async deleteMatchScreenshot(screenshotUrl) {
    try {
      if (!screenshotUrl) return;

      // Extract file path from public URL
      const urlParts = screenshotUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const matchId = urlParts[urlParts.length - 2];
      
      if (!fileName || !matchId) {
        throw new Error('Invalid screenshot URL format');
      }

      const filePath = `${matchId}/${fileName}`;

      const { error } = await supabase.storage
        .from('match-evidence')
        .remove([filePath]);

      if (error) {
        throw new Error(`Failed to delete screenshot: ${error.message}`);
      }

    } catch (error) {
      console.error('Error deleting match screenshot:', error);
      throw error;
    }
  }

  /**
   * Get all screenshots for a specific match
   * Used for displaying evidence gallery in match details
   */
  static async getMatchScreenshots(matchId) {
    try {
      const { data: files, error } = await supabase.storage
        .from('match-evidence')
        .list(matchId, {
          limit: 100,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        throw new Error(`Failed to list match screenshots: ${error.message}`);
      }

      if (!files || files.length === 0) {
        return [];
      }

      // Convert file list to public URLs with metadata
      const screenshots = files.map(file => {
        const { data: urlData } = supabase.storage
          .from('match-evidence')
          .getPublicUrl(`${matchId}/${file.name}`);

        // Parse team number from filename
        const teamMatch = file.name.match(/team(\d+)_screenshot/);
        const teamNumber = teamMatch ? parseInt(teamMatch[1]) : null;

        return {
          url: urlData.publicUrl,
          fileName: file.name,
          size: file.metadata?.size || 0,
          createdAt: file.created_at,
          updatedAt: file.updated_at,
          teamNumber,
          isTeam1: teamNumber === 1,
          isTeam2: teamNumber === 2
        };
      });

      return screenshots;
    } catch (error) {
      console.error('Error getting match screenshots:', error);
      throw error instanceof Error ? error : new Error('Failed to retrieve match screenshots');
    }
  }

  /**
   * Upload multiple screenshots for a match (e.g., Bo3/Bo5 series)
   * Returns array of upload results
   */
  static async uploadMultipleMatchScreenshots(matchId, files, teamNumber, submittedBy = null) {
    try {
      if (!Array.isArray(files) || files.length === 0) {
        throw new Error('Files array is required');
      }

      const uploadPromises = files.map((file, index) => 
        this.uploadMatchScreenshot(matchId, file, teamNumber, submittedBy)
          .catch(error => ({
            error: error.message,
            fileIndex: index,
            fileName: file.name
          }))
      );

      const results = await Promise.all(uploadPromises);
      
      const successful = results.filter(result => !result.error);
      const failed = results.filter(result => result.error);

      return {
        successful,
        failed,
        totalUploaded: successful.length,
        totalFailed: failed.length,
        urls: successful.map(result => result.url)
      };
    } catch (error) {
      console.error('Error uploading multiple match screenshots:', error);
      throw error instanceof Error ? error : new Error('Bulk screenshot upload failed');
    }
  }
}